/**
 * VenueMaps - Interactive floor plan with zones and points of interest
 * Supports drag-and-drop POI placement for admins
 */
import React, { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { MediaUpload } from '@/components/MediaUpload';
import {
  MapPin, Plus, Coffee, Utensils, Users, Mic,
  DoorOpen, Loader2, Edit2, Trash2, Info, Wifi, Car,
  Building, Accessibility, ShieldCheck, Camera, GripVertical, Move
} from 'lucide-react';
import api from '@/lib/api';
import GoogleMapPicker from '@/components/GoogleMapPicker';

const POI_TYPES = [
  { id: 'stage', name: 'Stage/Hall', icon: Mic, color: 'bg-purple-500' },
  { id: 'registration', name: 'Registration', icon: DoorOpen, color: 'bg-blue-500' },
  { id: 'food', name: 'Food & Drinks', icon: Utensils, color: 'bg-orange-500' },
  { id: 'restroom', name: 'Restrooms', icon: Users, color: 'bg-slate-500' },
  { id: 'networking', name: 'Networking Area', icon: Coffee, color: 'bg-green-500' },
  { id: 'info', name: 'Info Desk', icon: Info, color: 'bg-cyan-500' },
  { id: 'wifi', name: 'WiFi Zone', icon: Wifi, color: 'bg-indigo-500' },
  { id: 'parking', name: 'Parking', icon: Car, color: 'bg-gray-500' },
  { id: 'emergency', name: 'Emergency Exit', icon: ShieldCheck, color: 'bg-red-500' },
  { id: 'accessible', name: 'Accessibility', icon: Accessibility, color: 'bg-teal-500' },
  { id: 'photo', name: 'Photo Booth', icon: Camera, color: 'bg-pink-500' },
  { id: 'other', name: 'Other', icon: MapPin, color: 'bg-slate-400' }
];

const FLOORS = ['Ground Floor', 'Floor 1', 'Floor 2', 'Floor 3', 'Outdoor'];

// Draggable POI Marker Component
const DraggablePOI = ({ poi, poiType, isSelected, isAdmin, onSelect, onDragEnd, onEdit, onDelete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const markerRef = useRef(null);
  const Icon = poiType.icon;

  const handleMouseDown = useCallback((e) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.stopPropagation();
    
    const rect = markerRef.current.parentElement.getBoundingClientRect();
    const startX = ((e.clientX - rect.left) / rect.width) * 100;
    const startY = ((e.clientY - rect.top) / rect.height) * 100;
    
    setDragOffset({ x: startX - poi.position_x, y: startY - poi.position_y });
    setIsDragging(true);
  }, [isAdmin, poi.position_x, poi.position_y]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const rect = markerRef.current.parentElement.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100 - dragOffset.x));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100 - dragOffset.y));
    
    // Update position in real-time
    if (markerRef.current) {
      markerRef.current.style.left = `${x}%`;
      markerRef.current.style.top = `${y}%`;
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback((e) => {
    if (!isDragging) return;
    
    const rect = markerRef.current.parentElement.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100 - dragOffset.x));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100 - dragOffset.y));
    
    setIsDragging(false);
    onDragEnd(poi.id, Math.round(x), Math.round(y));
  }, [isDragging, dragOffset, poi.id, onDragEnd]);

  // Add global mouse listeners when dragging
  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      ref={markerRef}
      className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all ${
        isDragging ? 'scale-150 z-50 cursor-grabbing' : isSelected ? 'scale-125 z-20' : 'hover:scale-110 z-10'
      } ${isAdmin ? 'cursor-grab' : 'cursor-pointer'}`}
      style={{ left: `${poi.position_x}%`, top: `${poi.position_y}%` }}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        if (!isDragging) {
          e.stopPropagation();
          onSelect(isSelected ? null : poi);
        }
      }}
      data-testid={`poi-marker-${poi.id}`}
    >
      <div className={`${poiType.color} text-white p-2 rounded-full shadow-lg ${
        isSelected ? 'ring-4 ring-white ring-offset-2' : ''
      } ${isDragging ? 'ring-4 ring-blue-400 ring-offset-2' : ''}`}>
        <Icon className="h-5 w-5" />
      </div>
      
      {/* Drag indicator for admin */}
      {isAdmin && !isDragging && (
        <div className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow opacity-0 hover:opacity-100 transition-opacity">
          <Move className="h-3 w-3 text-slate-500" />
        </div>
      )}
      
      {/* Info popup when selected */}
      {isSelected && !isDragging && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white rounded-lg shadow-xl p-3 min-w-0 sm:min-w-[200px] text-left z-30">
          <p className="font-semibold">{poi.name}</p>
          <Badge className={`${poiType.color} text-white text-xs mt-1`}>
            {poiType.name}
          </Badge>
          {poi.description && (
            <p className="text-sm text-slate-600 mt-2">{poi.description}</p>
          )}
          {isAdmin && (
            <>
              <p className="text-xs text-slate-400 mt-2">
                Position: ({poi.position_x}%, {poi.position_y}%)
              </p>
              <div className="flex gap-2 mt-3 pt-2 border-t">
                <Button size="sm" variant="outline" onClick={() => onEdit(poi)}>
                  <Edit2 className="h-3 w-3 mr-1" /> Edit
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="text-red-500"
                  onClick={() => onDelete(poi.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const VenueMaps = ({ eventId, isAdmin = false }) => {
  const queryClient = useQueryClient();
  const [selectedFloor, setSelectedFloor] = useState('Ground Floor');
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [isAddingPOI, setIsAddingPOI] = useState(false);
  const [editingPOI, setEditingPOI] = useState(null);
  const [viewMode, setViewMode] = useState('floorplan'); // 'floorplan' | 'googlemap'
  const [venueSettings, setVenueSettings] = useState({
    name: '',
    address: '',
    map_url: '',
    floor_plan_url: '',
    wifi_ssid: '',
    wifi_password: '',
    directions: ''
  });
  const [showVenueSettings, setShowVenueSettings] = useState(false);
  const [newPOI, setNewPOI] = useState({
    name: '',
    type: 'other',
    floor: 'Ground Floor',
    description: '',
    position_x: 50,
    position_y: 50
  });

  // Fetch venue info
  const { data: venueInfo } = useQuery({
    queryKey: ['venue-info', eventId],
    queryFn: () => api.get(`/events/${eventId}/venue`).then(r => r.data),
  });

  // Update venue settings when data loads
  React.useEffect(() => {
    if (venueInfo) {
      setVenueSettings(prev => ({ ...prev, ...venueInfo }));
    }
  }, [venueInfo]);

  // Save venue settings mutation
  const saveVenueMutation = useMutation({
    mutationFn: (data) => api.put(`/admin/events/${eventId}/venue`, data),
    onSuccess: () => {
      toast.success('Venue settings saved!');
      queryClient.invalidateQueries(['venue-info', eventId]);
      setShowVenueSettings(false);
    },
    onError: () => toast.error('Failed to save venue settings')
  });

  // Fetch POIs
  const { data: pois = [], isLoading } = useQuery({
    queryKey: ['venue-pois', eventId],
    queryFn: () => api.get(`/events/${eventId}/venue/pois`).then(r => r.data),
  });

  // Create POI mutation
  const createPOIMutation = useMutation({
    mutationFn: (poi) => api.post(`/events/${eventId}/venue/pois`, poi),
    onSuccess: () => {
      toast.success('Location added!');
      queryClient.invalidateQueries(['venue-pois', eventId]);
      setIsAddingPOI(false);
      setNewPOI({ name: '', type: 'other', floor: 'Ground Floor', description: '', position_x: 50, position_y: 50 });
    },
    onError: () => toast.error('Failed to add location')
  });

  // Update POI mutation
  const updatePOIMutation = useMutation({
    mutationFn: ({ poiId, updates }) => api.put(`/events/${eventId}/venue/pois/${poiId}`, updates),
    onSuccess: () => {
      toast.success('Location updated!');
      queryClient.invalidateQueries(['venue-pois', eventId]);
      setEditingPOI(null);
    }
  });

  // Delete POI mutation
  const deletePOIMutation = useMutation({
    mutationFn: (poiId) => api.delete(`/events/${eventId}/venue/pois/${poiId}`),
    onSuccess: () => {
      toast.success('Location removed');
      queryClient.invalidateQueries(['venue-pois', eventId]);
      setSelectedPOI(null);
    }
  });

  const floorPOIs = pois.filter(p => p.floor === selectedFloor);
  const getPOIType = (typeId) => POI_TYPES.find(t => t.id === typeId) || POI_TYPES[POI_TYPES.length - 1];

  // Handle map click for placing POI
  const handleMapClick = (e) => {
    if (!isAdmin || !isAddingPOI) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setNewPOI(prev => ({ ...prev, position_x: Math.round(x), position_y: Math.round(y) }));
  };

  // Handle drag end for POI repositioning
  const handleDragEnd = useCallback((poiId, newX, newY) => {
    updatePOIMutation.mutate({ poiId, updates: { position_x: newX, position_y: newY } });
  }, [updatePOIMutation]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="venue-maps">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Building className="h-6 w-6 text-blue-500" />
            Venue Map
          </h2>
          <p className="text-slate-500">
            {isAdmin ? 'Drag markers to reposition • Click "Add Location" to create new POIs' : 'Navigate the event venue'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex rounded-lg border bg-slate-100 p-1">
            <Button
              size="sm"
              variant={viewMode === 'floorplan' ? 'default' : 'ghost'}
              onClick={() => setViewMode('floorplan')}
              className="text-xs"
            >
              <MapPin className="h-3 w-3 mr-1" />
              Floor Plan
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'googlemap' ? 'default' : 'ghost'}
              onClick={() => setViewMode('googlemap')}
              className="text-xs"
            >
              <Building className="h-3 w-3 mr-1" />
              Google Map
            </Button>
          </div>
          
          {isAdmin && (
            <>
              <Button variant="outline" onClick={() => setShowVenueSettings(true)}>
                <Edit2 className="h-4 w-4 mr-2" />
                Venue Settings
              </Button>
              <Dialog open={isAddingPOI} onOpenChange={setIsAddingPOI}>
                <DialogTrigger asChild>
                  <Button data-testid="add-poi-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Location
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Venue Location</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <Input
                      placeholder="Location name (e.g., Main Stage)"
                      value={newPOI.name}
                      onChange={(e) => setNewPOI({ ...newPOI, name: e.target.value })}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Type</label>
                        <Select value={newPOI.type} onValueChange={(v) => setNewPOI({ ...newPOI, type: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {POI_TYPES.map(t => {
                              const TypeIcon = t.icon;
                              return (
                                <SelectItem key={t.id} value={t.id}>
                                  <div className="flex items-center gap-2">
                                    <TypeIcon className="h-4 w-4" />
                                    {t.name}
                                  </div>
                                </SelectItem>
                              );
                            })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Floor</label>
                    <Select value={newPOI.floor} onValueChange={(v) => setNewPOI({ ...newPOI, floor: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FLOORS.map(f => (
                          <SelectItem key={f} value={f}>{f}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Textarea
                  placeholder="Description or directions (optional)"
                  value={newPOI.description}
                  onChange={(e) => setNewPOI({ ...newPOI, description: e.target.value })}
                  rows={2}
                />
                <p className="text-sm text-slate-500">
                  Click on the map to set the location, or enter coordinates:
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">X Position (%)</label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={newPOI.position_x}
                      onChange={(e) => setNewPOI({ ...newPOI, position_x: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Y Position (%)</label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={newPOI.position_y}
                      onChange={(e) => setNewPOI({ ...newPOI, position_y: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsAddingPOI(false)}>Cancel</Button>
                  <Button 
                    onClick={() => createPOIMutation.mutate(newPOI)}
                    disabled={!newPOI.name || createPOIMutation.isPending}
                  >
                    Add Location
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Venue Settings Dialog */}
      <Dialog open={showVenueSettings} onOpenChange={setShowVenueSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Venue Settings</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="location">Location</TabsTrigger>
              <TabsTrigger value="amenities">Amenities</TabsTrigger>
            </TabsList>
            
            <TabsContent value="details" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Venue Name</label>
                  <Input
                    value={venueSettings.name}
                    onChange={(e) => setVenueSettings({ ...venueSettings, name: e.target.value })}
                    placeholder="Convention Center"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">City</label>
                  <Input
                    value={venueSettings.city || ''}
                    onChange={(e) => setVenueSettings({ ...venueSettings, city: e.target.value })}
                    placeholder="New York"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Full Address</label>
                <Input
                  value={venueSettings.address}
                  onChange={(e) => setVenueSettings({ ...venueSettings, address: e.target.value })}
                  placeholder="123 Main Street, City, Country"
                />
              </div>
              
              <div className="space-y-2">
                <MediaUpload
                  value={venueSettings.floor_plan_url}
                  onChange={(url) => setVenueSettings({ ...venueSettings, floor_plan_url: url })}
                  purpose="banner"
                  label="Floor plan image (optional)"
                  testId="venue-floor-plan-upload"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Directions / Parking Info</label>
                <Textarea
                  value={venueSettings.directions}
                  onChange={(e) => setVenueSettings({ ...venueSettings, directions: e.target.value })}
                  placeholder="Enter directions, parking information, transit options..."
                  rows={3}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="location" className="space-y-4 pt-4">
              <div className="text-sm text-slate-500 mb-2">
                <MapPin className="h-4 w-4 inline mr-1" />
                Search and select the venue location on the map
              </div>
              <GoogleMapPicker
                latitude={venueSettings.latitude ? parseFloat(venueSettings.latitude) : null}
                longitude={venueSettings.longitude ? parseFloat(venueSettings.longitude) : null}
                address={venueSettings.address || ''}
                onLocationChange={(location) => {
                  setVenueSettings(prev => ({
                    ...prev,
                    latitude: location.lat,
                    longitude: location.lng,
                    address: location.address || prev.address,
                    city: location.city || prev.city,
                    map_url: location.lat && location.lng 
                      ? `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${location.lat},${location.lng}` 
                      : prev.map_url
                  }));
                }}
                height="300px"
                placeholder="Search for venue location..."
              />
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Custom Google Maps Embed URL (optional)</label>
                <Input
                  value={venueSettings.map_url}
                  onChange={(e) => setVenueSettings({ ...venueSettings, map_url: e.target.value })}
                  placeholder="https://www.google.com/maps/embed?pb=..."
                />
                <p className="text-xs text-slate-500">
                  Override automatic embed: Go to Google Maps → Search location → Share → Embed a map
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="amenities" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">WiFi Network Name</label>
                  <Input
                    value={venueSettings.wifi_ssid}
                    onChange={(e) => setVenueSettings({ ...venueSettings, wifi_ssid: e.target.value })}
                    placeholder="VenueWiFi"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">WiFi Password</label>
                  <Input
                    value={venueSettings.wifi_password}
                    onChange={(e) => setVenueSettings({ ...venueSettings, wifi_password: e.target.value })}
                    placeholder="password123"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowVenueSettings(false)}>Cancel</Button>
            <Button 
              onClick={() => saveVenueMutation.mutate(venueSettings)}
              disabled={saveVenueMutation.isPending}
            >
              {saveVenueMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Venue Settings
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Drag hint for admin */}
      {isAdmin && floorPOIs.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2 text-sm text-blue-700">
          <GripVertical className="h-4 w-4" />
          <span><strong>Tip:</strong> Drag and drop markers to reposition them on the map</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Map Area */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader className="pb-2">
              {/* Floor Selector - only show for floorplan view */}
              {viewMode === 'floorplan' && (
                <div className="flex items-center gap-2 overflow-x-auto pb-2">
                  {FLOORS.map(floor => (
                    <Button
                      key={floor}
                      variant={selectedFloor === floor ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedFloor(floor)}
                    >
                      {floor}
                      {pois.filter(p => p.floor === floor).length > 0 && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {pois.filter(p => p.floor === floor).length}
                        </Badge>
                      )}
                    </Button>
                  ))}
                </div>
              )}
              {viewMode === 'googlemap' && venueSettings.name && (
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{venueSettings.name}</h3>
                    {venueSettings.address && <p className="text-sm text-slate-500">{venueSettings.address}</p>}
                  </div>
                  {venueSettings.address && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venueSettings.address)}`, '_blank')}
                    >
                      Open in Google Maps
                    </Button>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {/* Google Maps View */}
              {viewMode === 'googlemap' && (
                <div 
                  className="relative bg-slate-100 rounded-lg border overflow-hidden"
                  style={{ aspectRatio: '16/9', minHeight: '400px' }}
                >
                  {venueSettings.map_url ? (
                    <iframe
                      src={venueSettings.map_url}
                      className="w-full h-full border-0"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Venue Location"
                    />
                  ) : venueSettings.address ? (
                    <iframe
                      src={`https://www.google.com/maps?q=${encodeURIComponent(venueSettings.address)}&output=embed`}
                      className="w-full h-full border-0"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Venue Location"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-slate-500">
                        <MapPin className="h-12 w-12 mx-auto mb-2 opacity-30" />
                        <p>No venue address configured</p>
                        {isAdmin && <p className="text-sm">Click "Venue Settings" to add an address</p>}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Floor Plan View */}
              {viewMode === 'floorplan' && (
              <div 
                className={`relative bg-slate-100 rounded-lg border-2 ${isAddingPOI ? 'border-blue-400 cursor-crosshair' : 'border-dashed border-slate-300'} overflow-hidden`}
                style={{ aspectRatio: '16/9', minHeight: '400px' }}
                onClick={handleMapClick}
                data-testid="venue-map-canvas"
              >
                {/* Custom floor plan image if uploaded */}
                {venueSettings.floor_plan_url && (
                  <img 
                    src={venueSettings.floor_plan_url} 
                    alt="Floor Plan" 
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                )}
                
                {/* Map background grid (show if no custom floor plan) */}
                {!venueSettings.floor_plan_url && (
                <div className="absolute inset-0 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 grid-rows-6 gap-px opacity-20">
                  {Array.from({ length: 48 }).map((_, i) => (
                    <div key={i} className="bg-slate-300" />
                  ))}
                </div>
                )}
                
                {/* Floor label */}
                <div className="absolute top-4 left-4 bg-white/90 px-3 py-1 rounded-full text-sm font-medium shadow">
                  {selectedFloor}
                </div>

                {/* POI Markers with drag support */}
                {floorPOIs.map(poi => {
                  const poiType = getPOIType(poi.type);
                  const isSelected = selectedPOI?.id === poi.id;
                  
                  return (
                    <DraggablePOI
                      key={poi.id}
                      poi={poi}
                      poiType={poiType}
                      isSelected={isSelected}
                      isAdmin={isAdmin}
                      onSelect={setSelectedPOI}
                      onDragEnd={handleDragEnd}
                      onEdit={setEditingPOI}
                      onDelete={(id) => deletePOIMutation.mutate(id)}
                    />
                  );
                })}

                {/* New POI preview when adding */}
                {isAdmin && isAddingPOI && (
                  <div
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-none animate-pulse"
                    style={{ left: `${newPOI.position_x}%`, top: `${newPOI.position_y}%` }}
                  >
                    <div className="bg-blue-500 text-white p-2 rounded-full shadow-lg ring-4 ring-blue-200">
                      <Plus className="h-5 w-5" />
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {floorPOIs.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-slate-500">
                      <MapPin className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>No locations added for this floor</p>
                      {isAdmin && <p className="text-sm">Click &ldquo;Add Location&rdquo; to get started</p>}
                    </div>
                  </div>
                )}
              </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Legend & POI List */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Legend</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[200px]">
                <div className="space-y-2">
                  {POI_TYPES.map(type => {
                    const TypeIcon = type.icon;
                    const count = pois.filter(p => p.type === type.id).length;
                    if (count === 0 && !isAdmin) return null;
                    
                    return (
                      <div key={type.id} className="flex items-center gap-2 text-sm">
                        <div className={`${type.color} text-white p-1 rounded`}>
                          <TypeIcon className="h-3 w-3" />
                        </div>
                        <span className="flex-1">{type.name}</span>
                        <Badge variant="secondary" className="text-xs">{count}</Badge>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* POI List for current floor */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{selectedFloor} Locations</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[200px]">
                {floorPOIs.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No locations on this floor</p>
                ) : (
                  <div className="space-y-2">
                    {floorPOIs.map(poi => {
                      const poiType = getPOIType(poi.type);
                      const TypeIcon = poiType.icon;
                      return (
                        <button
                          key={poi.id}
                          className={`w-full text-left p-2 rounded-lg hover:bg-slate-100 transition-colors ${
                            selectedPOI?.id === poi.id ? 'bg-slate-100 ring-2 ring-blue-500' : ''
                          }`}
                          onClick={() => setSelectedPOI(selectedPOI?.id === poi.id ? null : poi)}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`${poiType.color} text-white p-1 rounded`}>
                              <TypeIcon className="h-3 w-3" />
                            </div>
                            <span className="text-sm font-medium truncate">{poi.name}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit POI Dialog */}
      <Dialog open={!!editingPOI} onOpenChange={() => setEditingPOI(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Location</DialogTitle>
          </DialogHeader>
          {editingPOI && (
            <div className="space-y-4 pt-4">
              <Input
                placeholder="Location name"
                value={editingPOI.name}
                onChange={(e) => setEditingPOI({ ...editingPOI, name: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Type</label>
                  <Select value={editingPOI.type} onValueChange={(v) => setEditingPOI({ ...editingPOI, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {POI_TYPES.map(t => {
                        const TypeIcon = t.icon;
                        return (
                          <SelectItem key={t.id} value={t.id}>
                            <div className="flex items-center gap-2">
                              <TypeIcon className="h-4 w-4" />
                              {t.name}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Floor</label>
                  <Select value={editingPOI.floor} onValueChange={(v) => setEditingPOI({ ...editingPOI, floor: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FLOORS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Textarea
                placeholder="Description"
                value={editingPOI.description || ''}
                onChange={(e) => setEditingPOI({ ...editingPOI, description: e.target.value })}
                rows={2}
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">X Position (%)</label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={editingPOI.position_x}
                    onChange={(e) => setEditingPOI({ ...editingPOI, position_x: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Y Position (%)</label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={editingPOI.position_y}
                    onChange={(e) => setEditingPOI({ ...editingPOI, position_y: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingPOI(null)}>Cancel</Button>
                <Button 
                  onClick={() => updatePOIMutation.mutate({ poiId: editingPOI.id, updates: editingPOI })}
                  disabled={!editingPOI.name || updatePOIMutation.isPending}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VenueMaps;
